// Log in as its own page (/login). Step one asks for the email and checks it has a login; step two is Auth0's
// own page with that email already filled in, so only the password is typed. Also where the backend bounces
// a failed sign-in (?error=…), so the page says so and the retry doesn't force re-authentication.
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { MARKETING } from "../theme/ThemeContext";
import { useAuth } from "../context/AuthContext";
import AuthShell from "../components/AuthShell";
import { PrimaryButton, SANS } from "./LandingPage";

const C = MARKETING;

const Heading = styled.h1`
  font-family: ${SANS};
  font-weight: 600;
  font-size: 24px;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: ${C.deep};
  margin: 0;
  text-align: center;
`;

const Lead = styled.p`
  margin: 10px 0 24px;
  text-align: center;
  font-size: 15.5px;
  line-height: 1.55;
  color: ${C.body};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 13px;
  font-weight: 500;
  color: ${C.bodyStrong};
  input {
    font-family: ${SANS};
    font-size: 15px;
    color: ${C.ink};
    background: ${C.wash};
    border: 1px solid ${C.line};
    border-radius: 12px;
    padding: 13px 14px;
    outline: none;
    transition:
      border-color 0.16s ease,
      background 0.16s ease,
      box-shadow 0.16s ease;
    @media (max-width: 560px) {
      font-size: 16px;
    }
  }
  input:hover {
    border-color: ${C.border};
  }
  input:focus {
    background: ${C.onDark};
    border-color: ${C.blue};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${C.blue} 14%, transparent);
  }
`;

const Note = styled.div`
  margin: 0 0 18px;
  padding: 11px 14px;
  border-radius: 12px;
  background: ${({ $tone }) => ($tone === "info" ? C.blueWash : C.orangeWash)};
  border: 1px solid ${({ $tone }) => ($tone === "info" ? `color-mix(in srgb, ${C.blue} 30%, transparent)` : `color-mix(in srgb, ${C.orange} 30%, transparent)`)};
  color: ${({ $tone }) => ($tone === "info" ? C.deep : C.orangeInk)};
  font-size: 13.5px;
  line-height: 1.5;
  b {
    font-weight: 600;
  }
  button {
    border: 0;
    background: none;
    font: inherit;
    font-weight: 500;
    color: ${C.blue};
    cursor: pointer;
    padding: 0;
    min-height: 0;
  }
`;

const Switch = styled.p`
  margin: 18px 0 0;
  text-align: center;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${C.muted};
  button {
    border: 0;
    background: none;
    font: inherit;
    color: ${C.blue};
    cursor: pointer;
    padding: 0;
    min-height: 0;
  }
`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage() {
  const navigate = useNavigate();
  // `sessionInvalidated` is true for every signed-out visitor (the session check answers 401), so it only
  // decides whether the redirect asks Auth0 for fresh credentials; it is not a sign the session expired.
  const { login, sessionInvalidated } = useAuth();
  // The sign-up form hands over an address that already has an account (?email=).
  const [email, setEmail] = useState(() => {
    try {
      return (new URLSearchParams(window.location.search).get("email") || "").trim();
    } catch {
      return "";
    }
  });
  const [status, setStatus] = useState("idle"); // idle | checking | redirecting
  const [error, setError] = useState("");
  const [unknown, setUnknown] = useState(""); // the email that has no account yet
  const inputRef = useRef(null);
  // The BFF callback's ?error=… : the person did authenticate, so the retry must not force a fresh login.
  const [callbackError] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const err = q.get("error") || "";
      if (err) {
        console.warn("Sign-in callback error:", err, q.get("error_description") || q.get("reason") || "");
        window.history.replaceState(null, "", window.location.pathname);
      }
      return err;
    } catch {
      return "";
    }
  });
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);
  // Back from the Auth0 page restores this page from the bfcache with the redirect still pending; re-enable the form.
  useEffect(() => {
    const reset = () => setStatus("idle");
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (status !== "idle") return;
    const addr = email.trim().toLowerCase();
    setError("");
    setUnknown("");
    if (!EMAIL_RE.test(addr)) {
      setError("Please add a valid email.");
      return;
    }
    setStatus("checking");
    try {
      const res = await fetch("/api/auth/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: addr }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't check that email just now. Please try again.");
      if (!body.exists) {
        setUnknown(addr);
        setStatus("idle");
        return;
      }
      setStatus("redirecting");
      login({ loginHint: addr, reauth: sessionInvalidated && !callbackError }); // full-page Auth0 redirect, email prefilled
    } catch (err) {
      setError(err.message || "We couldn't check that email just now. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <AuthShell headline="Welcome back." sub="Your shortlist kept working while you were away. Log in to see who matched, who replied, and what to do next.">
      <Heading>Log in</Heading>
      <Lead>Enter your email. On the next page you only need your password.</Lead>
      {callbackError && (
        <Note role="alert">
          {callbackError === "invalid_token"
            ? "That sign-in link has expired. Log in again to get a fresh one."
            : "That sign-in didn't go through. Try again, and if it keeps happening, email support."}
        </Note>
      )}
      {unknown && (
        <Note role="status">
          There's no account for <b>{unknown}</b> yet.{" "}
          <button type="button" onClick={() => navigate(`/signup?email=${encodeURIComponent(unknown)}`)}>
            Sign up with this email
          </button>
        </Note>
      )}
      <form onSubmit={submit} noValidate style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field>
          Email
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setUnknown("");
            }}
            autoComplete="email"
            placeholder="you@yourfirm.com"
            required
          />
        </Field>
        {error && <div style={{ fontSize: 13.5, color: C.orangeInk }}>{error}</div>}
        <PrimaryButton type="submit" $lg disabled={status !== "idle"} style={{ width: "100%", marginTop: 4 }}>
          {status === "checking" ? "Checking…" : status === "redirecting" ? "Opening log in…" : "Continue"}
        </PrimaryButton>
      </form>
      <Switch>
        Don't have an account?{" "}
        <button type="button" onClick={() => navigate("/signup")}>
          Sign up
        </button>
      </Switch>
    </AuthShell>
  );
}
